import { useCallback, useMemo, useState, type ReactNode } from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Radio from "@mui/material/Radio";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {
  DataGridPremium,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  type GridColDef,
  type GridRowParams,
} from "@mui/x-data-grid-premium";
import eyeIcon from "../../assets/eye.svg";
import {
  formatTriageMatchedConcepts,
  type ManualProcessSummary,
  type RecommendedSubProcess,
} from "../../services/manualAssessmentService";
import { AiImpactedConceptsCell } from "./ImpactedConceptsChipCell";
import ProcessBpmnDialog, {
  type ProcessBpmnDialogTarget,
} from "./ProcessBpmnDialog";

export const PROCESS_TABLE_HEADER_HEIGHT = 48;
export const PROCESS_TABLE_ROW_HEIGHT = 64;
export const PROCESS_TABLE_HEIGHT = 545;

const NUNITO = '"Nunito Sans", sans-serif';

const MAIN_NAME_SX = {
  fontFamily: NUNITO,
  fontWeight: 800,
  fontSize: 14,
  lineHeight: "20px",
  letterSpacing: 0,
  color: "#262626",
} as const;

const CELL_TEXT_SX = {
  fontFamily: NUNITO,
  fontWeight: 400,
  fontSize: 14,
  lineHeight: "20px",
  color: "#262626",
} as const;

const HELPER_TEXT_SX = {
  fontFamily: NUNITO,
  fontWeight: 400,
  fontSize: 14,
  lineHeight: "20px",
  letterSpacing: 0,
  color: "#64748B",
} as const;

const RECOMMEND_CHIP_SX = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 88,
  height: 24,
  boxSizing: "border-box",
  px: "10px",
  py: "4px",
  borderRadius: "9999px",
  bgcolor: "#E7ECFC",
  color: "#295EF1",
  fontFamily: NUNITO,
  fontSize: 12,
  fontWeight: 600,
  lineHeight: "12px",
  letterSpacing: 0,
  textAlign: "center",
  flexShrink: 0,
} as const;

const SUB_NAME_ROW_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  minWidth: 0,
  gap: 1,
} as const;

const SUB_PROCESS_NAME_CLAMP_SX = {
  ...CELL_TEXT_SX,
  minWidth: 0,
  flex: 1,
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical" as const,
  overflow: "hidden",
  textOverflow: "ellipsis",
  wordBreak: "break-word",
  whiteSpace: "normal",
} as const;

const ROW_CONTROL_WIDTH = 80;
/** Matches main-row name cell left padding (sub-process checkbox sits after this). */
const SUB_ROW_NAME_CELL_PADDING_LEFT = "16px";
const SUB_ROW_BG = "#FBFCFD";

const COL_LAYOUT = {
  controls: {
    width: ROW_CONTROL_WIDTH,
    minWidth: ROW_CONTROL_WIDTH,
    maxWidth: ROW_CONTROL_WIDTH,
  },
  name: { width: 300, minWidth: 300, maxWidth: 300 },
  description: { flex: 1, minWidth: 160 },
  impactedConcepts: { flex: 1, minWidth: 160 },
  action: { width: 100, minWidth: 100, maxWidth: 100 },
} as const;

const ROW_CONTROL_SLOT_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  width: ROW_CONTROL_WIDTH,
} as const;

const PROCESS_NAME_ROW_SX = {
  display: "flex",
  alignItems: "center",
  gap: "14px",
  minWidth: 0,
  width: "100%",
  flexWrap: "nowrap",
} as const;

const ROW_CONTROL_INPUT_SX = {
  p: 0,
  flexShrink: 0,
  color: "#595959",
  "&.Mui-checked": {
    color: "#2953E8",
  },
  "&.MuiCheckbox-indeterminate": {
    color: "primary.main",
  },
  "& .MuiSvgIcon-root": { fontSize: 18 },
} as const;

/** MUI small checkbox/radio icon width — used to align select-all label 14px from control. */
const ROW_CONTROL_INPUT_SIZE = 18;

const SELECT_ALL_CHECKBOX_OFFSET_PX =
  (ROW_CONTROL_WIDTH - ROW_CONTROL_INPUT_SIZE) / 2;

/** Main-row process name start (controls + name padding + chevron + gap). */
const MAIN_PROCESS_NAME_START_PX =
  ROW_CONTROL_WIDTH +
  Number.parseInt(SUB_ROW_NAME_CELL_PADDING_LEFT, 10) +
  16 +
  Number.parseInt(PROCESS_NAME_ROW_SX.gap, 10);

const SELECT_ALL_LEADING_SX = {
  display: "flex",
  alignItems: "center",
  gap: "14px",
  pl: `${SELECT_ALL_CHECKBOX_OFFSET_PX}px`,
  minWidth: 0,
} as const;

const DETAIL_NAME_COMBINED_SX = {
  width: COL_LAYOUT.controls.width + COL_LAYOUT.name.width,
  minWidth: COL_LAYOUT.controls.minWidth + COL_LAYOUT.name.minWidth,
  maxWidth: COL_LAYOUT.controls.maxWidth + COL_LAYOUT.name.maxWidth,
  position: "relative",
  flexShrink: 0,
  boxSizing: "border-box",
  height: PROCESS_TABLE_ROW_HEIGHT,
} as const;

const SUB_PROCESS_CHECKBOX_ABSOLUTE_SX = {
  position: "absolute",
  left: `${SELECT_ALL_CHECKBOX_OFFSET_PX}px`,
  top: "50%",
  transform: "translateY(-50%)",
  display: "inline-flex",
  alignItems: "center",
} as const;

const SUB_PROCESS_NAME_ABSOLUTE_SX = {
  position: "absolute",
  left: `${MAIN_PROCESS_NAME_START_PX}px`,
  right: "16px",
  top: "50%",
  transform: "translateY(-50%)",
  minWidth: 0,
  display: "flex",
  alignItems: "center",
} as const;

const processTableGridSx = {
  border: "none",
  borderRadius: "8px",
  overflow: "hidden",
  "--DataGrid-rowBorderColor": "transparent",
  "& .MuiDataGrid-main": {
    position: "relative",
  },
  "& .MuiDataGrid-main::after": {
    content: '""',
    position: "absolute",
    top: 0,
    right: 0,
    width: 18,
    height: PROCESS_TABLE_HEADER_HEIGHT,
    backgroundColor: "#F9FAFB",
    borderBottom: "1px solid #F2F2F2",
    pointerEvents: "none",
    zIndex: 2,
  },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#F9FAFB",
    fontFamily: NUNITO,
    fontSize: "14px",
    fontWeight: 700,
    color: "#0D0D0D",
    minHeight: `${PROCESS_TABLE_HEADER_HEIGHT}px !important`,
    maxHeight: PROCESS_TABLE_HEADER_HEIGHT,
    borderBottom: "none",
  },
  "& .MuiDataGrid-topContainer": {
    backgroundColor: "#F9FAFB",
  },
  "& .MuiDataGrid-topContainer::after": {
    display: "none",
  },
  "& .MuiDataGrid-virtualScroller": {
    borderTop: "none",
  },
  "& .MuiDataGrid-columnHeader": {
    height: PROCESS_TABLE_HEADER_HEIGHT,
    maxHeight: PROCESS_TABLE_HEADER_HEIGHT,
    padding: "0 24px",
    backgroundColor: "#F9FAFB",
    borderBottom: "none",
  },
  "& .MuiDataGrid-columnHeadersInner, & .MuiDataGrid-columnHeader--filler, & .MuiDataGrid-filler, & .MuiDataGrid-scrollbarFiller":
    {
      backgroundColor: "#F9FAFB",
    },
  "& .MuiDataGrid-scrollbarFiller--header, & .MuiDataGrid-scrollbarFiller--borderTop, & .MuiDataGrid-filler--header, & .MuiDataGrid-filler--borderBottom":
    {
      backgroundColor: "#F9FAFB",
    },
  "& .MuiDataGrid-virtualScroller::before": {
    backgroundColor: "#F9FAFB",
  },
  '& .MuiDataGrid-columnHeader[data-field="rowControls"]': {
    padding: 0,
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontWeight: 700,
    fontSize: "14px",
  },
  "& .MuiDataGrid-row": {
    cursor: "default",
    maxHeight: `${PROCESS_TABLE_ROW_HEIGHT}px !important`,
    minHeight: `${PROCESS_TABLE_ROW_HEIGHT}px !important`,
    borderBottom: "none",
  },
  "& .MuiDataGrid-cell": {
    fontSize: "14px",
    color: "#262626",
    fontWeight: 500,
    alignItems: "center",
    display: "flex",
    maxHeight: `${PROCESS_TABLE_ROW_HEIGHT}px !important`,
    minHeight: `${PROCESS_TABLE_ROW_HEIGHT}px !important`,
    py: 0,
    overflow: "hidden",
    borderTop: "none",
    borderBottom: "1px solid #F2F2F2",
    padding: "0 24px",
  },
  '& .MuiDataGrid-cell[data-field="rowControls"]': {
    padding: 0,
  },
  '& .MuiDataGrid-cell[data-field="name"]': {
    padding: "0 16px",
  },
  "& .MuiDataGrid-cellContent": {
    width: "100%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    overflow: "hidden",
  },
  [`& .MuiDataGrid-columnHeader[data-field="${GRID_DETAIL_PANEL_TOGGLE_FIELD}"]`]:
    {
      display: "none",
    },
  [`& .MuiDataGrid-cell[data-field="${GRID_DETAIL_PANEL_TOGGLE_FIELD}"]`]: {
    display: "none",
  },
  "& .MuiDataGrid-detailPanel": {
    backgroundColor: "#FFFFFF",
    padding: 0,
    borderTop: "none",
    borderBottom: "none",
  },
  "& .MuiDataGrid-detailPanels": {
    padding: 0,
    borderTop: "none",
  },
  "& .MuiDataGrid-columnSeparator": {
    display: "none",
  },
} as const;

const VIEW_ICON_SX = { width: 20, height: 20, display: "block" } as const;

export type ManualAssessmentMainRow = Readonly<{
  id: number;
  name: string;
  description: string | null;
  subProcesses: readonly ManualProcessSummary[];
}>;

export type StartManualAssessmentProcessTableProps = Readonly<{
  rows: readonly ManualAssessmentMainRow[];
  selectedMainId: number | null;
  onSelectedMainIdChange: (id: number | null) => void;
  expandedMainIds: ReadonlySet<number>;
  onExpandedMainIdsChange: (ids: ReadonlySet<number>) => void;
  selectedSubIds: ReadonlySet<number>;
  onSelectedSubIdsChange: (ids: ReadonlySet<number>) => void;
  recommendedSubProcesses?: readonly RecommendedSubProcess[];
  showHelperText?: boolean;
  /** Triage (`POST .../triage-processes-recommendation`) only; hidden for process-templates. */
  showImpactedConceptsColumn?: boolean;
}>;

function matchesSearch(name: string, searchLower: string): boolean {
  return name.toLowerCase().includes(searchLower);
}

function getVisibleSubProcesses(
  row: ManualAssessmentMainRow,
  searchLower: string,
): ManualProcessSummary[] {
  if (!searchLower) return [...row.subProcesses];
  if (matchesSearch(row.name, searchLower)) return [...row.subProcesses];
  return row.subProcesses.filter((sp) => matchesSearch(sp.name, searchLower));
}

function RecommendChip() {
  return (
    <Box component="span" sx={RECOMMEND_CHIP_SX}>
      Recommend
    </Box>
  );
}

function SubProcessNameLabel({ name }: Readonly<{ name: string }>) {
  return (
    <Typography variant="body2" sx={SUB_PROCESS_NAME_CLAMP_SX} title={name}>
      {name}
    </Typography>
  );
}

type ExpandToggleButtonProps = Readonly<{
  expanded: boolean;
  label: string;
  onClick: () => void;
}>;

const EXPAND_TOGGLE_SLOT_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  width: 16,
} as const;

function ExpandToggleButton({ expanded, label, onClick }: ExpandToggleButtonProps) {
  return (
    <Box sx={EXPAND_TOGGLE_SLOT_SX}>
      <IconButton
        size="small"
        aria-label={label}
        aria-expanded={expanded}
        onClick={onClick}
        sx={{ p: 0.25 }}
      >
        <ChevronRightIcon
          sx={{
            fontSize: 20,
            color: "#64748B",
            transform: expanded ? "rotate(90deg)" : "none",
            transition: "transform 0.2s",
          }}
        />
      </IconButton>
    </Box>
  );
}

function ProcessViewIconButton({
  onClick,
  label,
}: Readonly<{ onClick: () => void; label: string }>) {
  return (
    <Tooltip title="View">
      <IconButton
        size="small"
        aria-label={`View ${label}`}
        onClick={onClick}
        sx={{ p: 0, width: 20, height: 20 }}
      >
        <Box component="img" src={eyeIcon} alt="" sx={VIEW_ICON_SX} />
      </IconButton>
    </Tooltip>
  );
}

type SelectAllRowCellsProps = Readonly<{
  checkbox: ReactNode;
  name: ReactNode;
  description: ReactNode;
  impactedConcepts?: ReactNode;
  action: ReactNode;
  showImpactedConceptsColumn: boolean;
}>;

/** Checkbox aligned with main-row radio; label sits beside checkbox (no chevron gap). */
function SelectAllRowCells({
  checkbox,
  name,
  description,
  impactedConcepts,
  action,
  showImpactedConceptsColumn,
}: SelectAllRowCellsProps) {
  return (
    <>
      <Box
        sx={{
          width: COL_LAYOUT.controls.width + COL_LAYOUT.name.width,
          minWidth: COL_LAYOUT.controls.minWidth + COL_LAYOUT.name.minWidth,
          maxWidth: COL_LAYOUT.controls.maxWidth + COL_LAYOUT.name.maxWidth,
          flexShrink: 0,
          boxSizing: "border-box",
        }}
      >
        <Box sx={SELECT_ALL_LEADING_SX}>
          {checkbox}
          {name}
        </Box>
      </Box>
      <Box
        sx={{
          ...COL_LAYOUT.description,
          display: "flex",
          alignItems: "center",
          minWidth: 0,
          px: "24px",
          boxSizing: "border-box",
        }}
      >
        {description}
      </Box>
      {showImpactedConceptsColumn ? (
        <Box
          sx={{
            ...COL_LAYOUT.impactedConcepts,
            display: "flex",
            alignItems: "center",
            minWidth: 0,
            height: PROCESS_TABLE_ROW_HEIGHT,
            px: "24px",
            boxSizing: "border-box",
          }}
        >
          {impactedConcepts}
        </Box>
      ) : null}
      <Box
        sx={{
          width: COL_LAYOUT.action.width,
          minWidth: COL_LAYOUT.action.minWidth,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          flexShrink: 0,
          px: "24px",
          boxSizing: "border-box",
        }}
      >
        {action}
      </Box>
    </>
  );
}

type SubProcessRowCellsProps = Readonly<{
  rowLeading: ReactNode;
  name: ReactNode;
  description: ReactNode;
  impactedConcepts?: ReactNode;
  action: ReactNode;
  showImpactedConceptsColumn: boolean;
}>;

/** Sub-process row: checkbox aligned with select-all; name keeps prior indent. */
function SubProcessRowCells({
  rowLeading,
  name,
  description,
  impactedConcepts,
  action,
  showImpactedConceptsColumn,
}: SubProcessRowCellsProps) {
  return (
    <>
      <Box sx={DETAIL_NAME_COMBINED_SX}>
        <Box sx={SUB_PROCESS_CHECKBOX_ABSOLUTE_SX}>{rowLeading}</Box>
        <Box sx={SUB_PROCESS_NAME_ABSOLUTE_SX}>{name}</Box>
      </Box>
      <Box
        sx={{
          ...COL_LAYOUT.description,
          display: "flex",
          alignItems: "center",
          minWidth: 0,
          px: "24px",
          boxSizing: "border-box",
        }}
      >
        {description}
      </Box>
      {showImpactedConceptsColumn ? (
        <Box
          sx={{
            ...COL_LAYOUT.impactedConcepts,
            display: "flex",
            alignItems: "center",
            minWidth: 0,
            height: PROCESS_TABLE_ROW_HEIGHT,
            px: "24px",
            boxSizing: "border-box",
          }}
        >
          {impactedConcepts}
        </Box>
      ) : null}
      <Box
        sx={{
          width: COL_LAYOUT.action.width,
          minWidth: COL_LAYOUT.action.minWidth,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          flexShrink: 0,
          px: "24px",
          boxSizing: "border-box",
        }}
      >
        {action}
      </Box>
    </>
  );
}

type SubDetailPanelProps = Readonly<{
  row: ManualAssessmentMainRow;
  visibleSubs: readonly ManualProcessSummary[];
  selectedSubIds: ReadonlySet<number>;
  onToggleSub: (subId: number, mainRowId: number) => void;
  onSelectAllSubs: () => void;
  recommendedSubProcesses: readonly RecommendedSubProcess[];
  onView: (target: ProcessBpmnDialogTarget) => void;
  showImpactedConceptsColumn: boolean;
}>;

function SubProcessDetailPanel({
  row,
  visibleSubs,
  selectedSubIds,
  onToggleSub,
  onSelectAllSubs,
  recommendedSubProcesses,
  onView,
  showImpactedConceptsColumn,
}: SubDetailPanelProps) {
  const recommendedIds = useMemo(
    () => new Set(recommendedSubProcesses.map((r) => r.id)),
    [recommendedSubProcesses],
  );
  const recommendedById = useMemo(() => {
    const map = new Map<number, RecommendedSubProcess>();
    for (const rec of recommendedSubProcesses) {
      map.set(rec.id, rec);
    }
    return map;
  }, [recommendedSubProcesses]);

  const allVisibleSelected =
    visibleSubs.length > 0 &&
    visibleSubs.every((sp) => selectedSubIds.has(sp.id));
  const someVisibleSelected = visibleSubs.some((sp) => selectedSubIds.has(sp.id));

  if (row.subProcesses.length === 0) {
    return (
      <Box sx={{ pl: 2, py: 1.5, pr: 2 }}>
        <Typography variant="body2" sx={CELL_TEXT_SX} color="text.secondary">
          No subprocesses for this main process.
        </Typography>
      </Box>
    );
  }

  return (
    <Box role="rowgroup" aria-label={`Subprocesses for ${row.name}`}>
      {visibleSubs.length > 0 ? (
        <Box
          role="row"
          sx={{
            display: "flex",
            alignItems: "center",
            borderBottom: "1px solid #F2F2F2",
            minHeight: PROCESS_TABLE_ROW_HEIGHT,
            maxHeight: PROCESS_TABLE_ROW_HEIGHT,
            bgcolor: SUB_ROW_BG,
          }}
        >
          <SelectAllRowCells
            checkbox={
              <Checkbox
                size="small"
                checked={allVisibleSelected}
                indeterminate={someVisibleSelected && !allVisibleSelected}
                onChange={onSelectAllSubs}
                sx={ROW_CONTROL_INPUT_SX}
                slotProps={{
                  input: { "aria-label": "Select all subprocesses" },
                }}
              />
            }
            name={
              <Typography
                component="button"
                type="button"
                onClick={onSelectAllSubs}
                sx={{
                  ...CELL_TEXT_SX,
                  border: "none",
                  bgcolor: "transparent",
                  cursor: "pointer",
                  p: 0,
                  textAlign: "left",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Select All Subprocesses
              </Typography>
            }
            description={null}
            impactedConcepts={null}
            action={null}
            showImpactedConceptsColumn={showImpactedConceptsColumn}
          />
        </Box>
      ) : null}

      {visibleSubs.length === 0 ? (
        <Box sx={{ pl: 2, py: 1.5, pr: 2 }}>
          <Typography variant="body2" sx={CELL_TEXT_SX} color="text.secondary">
            No subprocesses match your search.
          </Typography>
        </Box>
      ) : null}

      {visibleSubs.map((sp) => {
        const isRec = recommendedIds.has(sp.id);
        const impactedConceptsText = formatTriageMatchedConcepts(
          recommendedById.get(sp.id)?.matched_concepts,
        );
        return (
          <Box
            key={sp.id}
            role="row"
            sx={{
              display: "flex",
              alignItems: "center",
              borderBottom: "1px solid #F2F2F2",
              minHeight: PROCESS_TABLE_ROW_HEIGHT,
              maxHeight: PROCESS_TABLE_ROW_HEIGHT,
              bgcolor: SUB_ROW_BG,
            }}
          >
            <SubProcessRowCells
              rowLeading={
                <Checkbox
                  size="small"
                  checked={selectedSubIds.has(sp.id)}
                  onChange={() => onToggleSub(sp.id, row.id)}
                  sx={ROW_CONTROL_INPUT_SX}
                  slotProps={{
                    input: { "aria-label": `Select subprocess ${sp.name}` },
                  }}
                />
              }
              name={
                isRec ? (
                  <Box sx={SUB_NAME_ROW_SX}>
                    <SubProcessNameLabel name={sp.name} />
                    <RecommendChip />
                  </Box>
                ) : (
                  <SubProcessNameLabel name={sp.name} />
                )
              }
              description={
                <Typography
                  variant="body2"
                  sx={CELL_TEXT_SX}
                  color="text.secondary"
                  noWrap
                  title={sp.description ?? undefined}
                >
                  {sp.description?.trim() ? sp.description : "—"}
                </Typography>
              }
              impactedConcepts={
                showImpactedConceptsColumn ? (
                  <AiImpactedConceptsCell value={impactedConceptsText} />
                ) : undefined
              }
              action={
                <ProcessViewIconButton
                  label={sp.name}
                  onClick={() =>
                    onView({ processId: sp.id, name: sp.name })
                  }
                />
              }
              showImpactedConceptsColumn={showImpactedConceptsColumn}
            />
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * Master–detail process picker for Start Manual Assessment.
 * @see docs/requirements/assessment-center/manual-impact-assessment.md
 */
export default function StartManualAssessmentProcessTable({
  rows,
  selectedMainId,
  onSelectedMainIdChange,
  expandedMainIds,
  onExpandedMainIdsChange,
  selectedSubIds,
  onSelectedSubIdsChange,
  recommendedSubProcesses = [],
  showHelperText = true,
  showImpactedConceptsColumn = false,
}: StartManualAssessmentProcessTableProps) {
  const [search, setSearch] = useState("");
  const [viewTarget, setViewTarget] = useState<ProcessBpmnDialogTarget | null>(
    null,
  );

  const searchLower = search.trim().toLowerCase();

  const filteredRows = useMemo(() => {
    if (!searchLower) return [...rows];
    return rows.filter((row) => {
      if (matchesSearch(row.name, searchLower)) return true;
      return row.subProcesses.some((sp) => matchesSearch(sp.name, searchLower));
    });
  }, [rows, searchLower]);

  const toggleSub = useCallback(
    (subId: number, mainRowId: number) => {
      if (selectedSubIds.has(subId)) {
        const next = new Set(selectedSubIds);
        next.delete(subId);
        onSelectedSubIdsChange(next);
        return;
      }

      if (selectedMainId !== mainRowId) {
        onSelectedMainIdChange(mainRowId);
        onSelectedSubIdsChange(new Set([subId]));
        return;
      }

      const next = new Set(selectedSubIds);
      next.add(subId);
      onSelectedSubIdsChange(next);
    },
    [
      selectedMainId,
      selectedSubIds,
      onSelectedMainIdChange,
      onSelectedSubIdsChange,
    ],
  );

  const handleMainSelect = useCallback(
    (rowId: number) => {
      if (selectedMainId === rowId) return;
      onSelectedMainIdChange(rowId);
      onSelectedSubIdsChange(new Set());
    },
    [selectedMainId, onSelectedMainIdChange, onSelectedSubIdsChange],
  );

  const handleToggleExpand = useCallback(
    (rowId: number) => {
      const row = rows.find((entry) => entry.id === rowId);
      if (!row || row.subProcesses.length === 0) return;

      const next = new Set(expandedMainIds);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      onExpandedMainIdsChange(next);
    },
    [expandedMainIds, onExpandedMainIdsChange, rows],
  );

  const handleSelectAllSubs = useCallback(
    (row: ManualAssessmentMainRow, visibleSubs: readonly ManualProcessSummary[]) => {
      const allSelected = visibleSubs.every((sp) => selectedSubIds.has(sp.id));
      if (allSelected) {
        const next = new Set(selectedSubIds);
        for (const sp of visibleSubs) {
          next.delete(sp.id);
        }
        onSelectedSubIdsChange(next);
        return;
      }

      if (selectedMainId !== row.id) {
        onSelectedMainIdChange(row.id);
      }
      onSelectedSubIdsChange(new Set(visibleSubs.map((sp) => sp.id)));
    },
    [
      selectedMainId,
      selectedSubIds,
      onSelectedMainIdChange,
      onSelectedSubIdsChange,
    ],
  );

  const columns = useMemo<GridColDef<ManualAssessmentMainRow>[]>(() => {
    const defs: GridColDef<ManualAssessmentMainRow>[] = [
      {
        field: "rowControls",
        headerName: "",
        ...COL_LAYOUT.controls,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Box sx={ROW_CONTROL_SLOT_SX}>
            <Radio
              size="small"
              checked={selectedMainId === params.row.id}
              onChange={() => handleMainSelect(params.row.id)}
              name="manual-assessment-main-process"
              sx={ROW_CONTROL_INPUT_SX}
              slotProps={{
                input: {
                  "aria-label": `Select main process ${params.row.name}`,
                },
              }}
            />
          </Box>
        ),
      },
      {
        field: "name",
        headerName: "Process Name",
        ...COL_LAYOUT.name,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const hasSubProcesses = params.row.subProcesses.length > 0;
          const isExpanded = expandedMainIds.has(params.row.id);
          return (
            <Box sx={PROCESS_NAME_ROW_SX}>
              {hasSubProcesses ? (
                <ExpandToggleButton
                  expanded={isExpanded}
                  label={`${isExpanded ? "Collapse" : "Expand"} subprocesses for ${params.row.name}`}
                  onClick={() => handleToggleExpand(params.row.id)}
                />
              ) : null}
              <Typography
                component="span"
                variant="body2"
                sx={{
                  ...MAIN_NAME_SX,
                  minWidth: 0,
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                noWrap
                title={params.row.name}
              >
                {params.row.name}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "description",
        headerName: "Description",
        ...COL_LAYOUT.description,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const text = (params.value as string)?.trim();
          return (
            <Typography
              variant="body2"
              sx={MAIN_NAME_SX}
              noWrap
              title={text || undefined}
            >
              {text ? text : "—"}
            </Typography>
          );
        },
      },
    ];

    if (showImpactedConceptsColumn) {
      defs.push({
        field: "impactedConcepts",
        headerName: "Impacted Values",
        ...COL_LAYOUT.impactedConcepts,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: () => <AiImpactedConceptsCell value="—" />,
      });
    }

    defs.push({
      field: "action",
      headerName: "Action",
      ...COL_LAYOUT.action,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <ProcessViewIconButton
          label={params.row.name}
          onClick={() =>
            setViewTarget({
              processId: params.row.id,
              name: params.row.name,
            })
          }
        />
      ),
    });

    return defs;
  }, [
    selectedMainId,
    expandedMainIds,
    handleMainSelect,
    handleToggleExpand,
    showImpactedConceptsColumn,
  ]);

  const getDetailPanelContent = useCallback(
    ({ row }: GridRowParams<ManualAssessmentMainRow>) => {
      if (row.subProcesses.length === 0) return null;

      const visibleSubs = getVisibleSubProcesses(row, searchLower);
      return (
        <SubProcessDetailPanel
          row={row}
          visibleSubs={visibleSubs}
          selectedSubIds={selectedSubIds}
          onToggleSub={toggleSub}
          onSelectAllSubs={() => handleSelectAllSubs(row, visibleSubs)}
          recommendedSubProcesses={recommendedSubProcesses}
          onView={setViewTarget}
          showImpactedConceptsColumn={showImpactedConceptsColumn}
        />
      );
    },
    [
      searchLower,
      selectedSubIds,
      toggleSub,
      handleSelectAllSubs,
      recommendedSubProcesses,
      showImpactedConceptsColumn,
    ],
  );

  const getDetailPanelHeight = useCallback(() => "auto" as const, []);

  const expandedRowIds = useMemo(() => {
    const expandableMainIds = new Set(
      rows
        .filter((row) => row.subProcesses.length > 0)
        .map((row) => row.id),
    );
    return new Set(
      [...expandedMainIds].filter((id) => expandableMainIds.has(id)),
    );
  }, [expandedMainIds, rows]);

  const handleExpandedRowIdsChange = useCallback(
    (ids: Set<number | string>) => {
      onExpandedMainIdsChange(
        new Set([...ids].map((id) => Number(id))),
      );
    },
    [onExpandedMainIdsChange],
  );

  const gridRows = useMemo(
    () =>
      filteredRows.map((r) => ({
        ...r,
        description: r.description ?? "",
      })),
    [filteredRows],
  );

  const selectedSubCount = selectedSubIds.size;

  return (
    <Box>
      {showHelperText ? (
        <Typography sx={{ ...HELPER_TEXT_SX, mb: 1.5 }}>
          Review and select follow-up processes to start the manual assessment.
        </Typography>
      ) : null}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 1.5,
        }}
      >
        <TextField
          placeholder="Input process name"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          inputProps={{ "aria-label": "Input process name" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: 1 }}>
                <SearchIcon sx={{ color: "#94A3B8", fontSize: 16 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: 360,
            flexShrink: 0,
            "& .MuiOutlinedInput-root": {
              height: 36,
              borderRadius: "8px",
              bgcolor: "#FFFFFF",
              fontFamily: NUNITO,
              "& fieldset": {
                borderColor: "rgba(0, 0, 0, 0.1)",
                borderWidth: "0.571px",
              },
              "&:hover fieldset": {
                borderColor: "rgba(0, 0, 0, 0.2)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#2953E8",
              },
            },
            "& .MuiInputBase-input": { fontFamily: NUNITO, fontSize: 14 },
            "& .MuiInputBase-input::placeholder": {
              fontFamily: NUNITO,
              fontSize: 14,
              opacity: 1,
              color: "#94A3B8",
            },
          }}
        />
        {selectedSubCount > 0 ? (
          <Box
            sx={{
              bgcolor: "#F1F5F9",
              border: "1px solid #E2E8F0",
              borderRadius: "4px",
              px: "9px",
              py: "9px",
              flex: "0 0 auto",
            }}
          >
            <Typography
              sx={{
                fontFamily: NUNITO,
                fontWeight: 600,
                fontSize: 12,
                lineHeight: "12px",
                color: "#475569",
                whiteSpace: "nowrap",
              }}
            >
              {selectedSubCount} Selected
            </Typography>
          </Box>
        ) : null}
      </Box>

      <Box
        sx={{
          height: PROCESS_TABLE_HEIGHT,
          width: "100%",
        }}
      >
        <DataGridPremium
          rows={gridRows}
          columns={columns}
          getRowId={(row) => row.id}
          getRowHeight={() => PROCESS_TABLE_ROW_HEIGHT}
          disableRowSelectionOnClick
          hideFooter
          disableColumnFilter
          disableColumnMenu
          disableColumnSorting
          columnVisibilityModel={{
            [GRID_DETAIL_PANEL_TOGGLE_FIELD]: false,
          }}
          getDetailPanelContent={getDetailPanelContent}
          getDetailPanelHeight={getDetailPanelHeight}
          detailPanelExpandedRowIds={expandedRowIds}
          onDetailPanelExpandedRowIdsChange={handleExpandedRowIdsChange}
          density="standard"
          aria-label="Manual assessment process selection"
          sx={processTableGridSx}
        />
      </Box>

      <ProcessBpmnDialog
        target={viewTarget}
        onClose={() => setViewTarget(null)}
      />
    </Box>
  );
}
